/**
 * Shared helpers used by ChatsPage and its extracted sub-components
 * (MessageBubble, ZohoEmailTab, TemplateManager, etc.).
 *
 * Extracted from ChatsPage.jsx in Apr 2026 as part of the refactor sprint.
 */

// Non-Latin scripts that we always want to offer translation for.
// Devanagari (Hindi/Marathi/Sanskrit), Sinhala, Thai/Lao, Arabic, Cyrillic,
// CJK Unified, Hiragana, Katakana, Hangul.
const NON_LATIN_RE = /[\u0900-\u0DFF\u0E00-\u0E7F\u0600-\u06FF\u0400-\u04FF\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/;

// Hinglish + a few European-language hint words. We only translate latin text
// when at least one of these tokens appears AND the message is short — avoids
// pestering the user with a Translate button on long English paragraphs.
const HINGLISH_HINTS_RE = /\b(kya|hai|hain|nahi|nahin|aap|aapka|aapki|aapko|kaise|kaisa|kab|kahan|kyun|kyon|main|mera|meri|mujhe|tum|tumhara|theek|namaste|namaskar|dhanyavad|shukriya|bhai|bhaiya|behen|didi|sahi|galat|paisa|paise|kitna|kitni|order|deliver|kar|karo|karein|chahiye|milega|milegi|abhi|aaj|kal|haan|nahi|krpya|kripya|gracias|bonjour|merci|hola|por\sfavor)\b/i;

/**
 * Returns true if the given text *looks* non-English, meaning we should
 * offer a "Translate" button. We deliberately err on the side of showing
 * the button — false positives are harmless (clicking will just return the
 * same text from the LLM).
 */
export function looksNonEnglish(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  if (t.length < 2) return false;
  if (NON_LATIN_RE.test(t)) return true;
  // Pure URL / number / email → don't bother
  if (/^[\s\d+@./:,_-]+$/.test(t)) return false;
  if (/^https?:\/\//i.test(t) && t.split(/\s+/).length <= 2) return false;
  return HINGLISH_HINTS_RE.test(t);
}

/* ----------------------------------------------------------------
 * Chat-item visual helpers (extracted from ChatsPage.jsx, Feb 2026 —
 * ChatListSidebar refactor). Kept here so both the sidebar list and
 * the chat header badge can share the exact same logic.
 * --------------------------------------------------------------- */

// Avatar gradient colors for consistent per-user colors
export const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #E11D48, #F43F5E)",
  "linear-gradient(135deg, #7C3AED, #8B5CF6)",
  "linear-gradient(135deg, #0891B2, #06B6D4)",
  "linear-gradient(135deg, #EA580C, #F97316)",
  "linear-gradient(135deg, #9333EA, #A855F7)",
  "linear-gradient(135deg, #16A34A, #22C55E)",
  "linear-gradient(135deg, #2563EB, #3B82F6)",
  "linear-gradient(135deg, #DC2626, #EF4444)",
  "linear-gradient(135deg, #0D9488, #14B8A6)",
  "linear-gradient(135deg, #CA8A04, #EAB308)",
];

export const getAvatarGradient = (name) =>
  AVATAR_GRADIENTS[
    Math.abs([...(name || "")].reduce((a, c) => a + c.charCodeAt(0), 0)) %
      AVATAR_GRADIENTS.length
  ];

/**
 * Returns a little pill spec {text, color, bg} to show how long the
 * customer has been waiting for an agent reply. Returns null when no
 * timer should be shown (e.g. solved chats, agent-last messages).
 */
export const getResponseTimer = (chat) => {
  // No timer for solved chats (iter 132 — WATI status is the source of truth).
  if (chat?.wati_status === "SOLVED") return null;
  // Iter 156.17 — WhatsApp sessions expire 24h after the customer's last
  // message. Treat anything >= 24h (or wati_status === EXPIRED) as expired
  // so the pill stops growing unbounded ("23h" → "47h" → "71h" ...).
  if (chat?.wati_status === "EXPIRED") {
    return { text: "expired", color: "text-gray-500", bg: "bg-gray-100" };
  }
  const lastMsg = chat?.last_message;
  if (!lastMsg || lastMsg.direction !== "incoming") return null;
  const lastTime = new Date(lastMsg.timestamp || chat.last_message_at);
  const now = new Date();
  const diffMs = now - lastTime;
  if (diffMs < 0) return null;
  if (diffMs >= 24 * 60 * 60 * 1000) {
    return { text: "expired", color: "text-gray-500", bg: "bg-gray-100" };
  }
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return { text: "now", color: "text-green-600", bg: "bg-green-50" };
  if (mins < 5) return { text: `${mins}m`, color: "text-green-600", bg: "bg-green-50" };
  if (mins < 15) return { text: `${mins}m`, color: "text-yellow-600", bg: "bg-yellow-50" };
  if (mins < 60) return { text: `${mins}m`, color: "text-red-500", bg: "bg-red-50" };
  const hrs = Math.floor(mins / 60);
  const rm = mins % 60;
  return {
    text: rm > 0 ? `${hrs}h ${rm}m` : `${hrs}h`,
    color: "text-red-600",
    bg: "bg-red-50",
  };
};

/** Short, chat-list-style timestamp (time today / "Yesterday" / date). */
export const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000)
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  if (diff < 172800000) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/** Long-form timestamp shown on individual message bubbles. */
export const formatFullTime = (ts) =>
  ts
    ? new Date(ts).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/**
 * Groups a flat list of messages into day-sections for the chat thread UI.
 * Each section has `{ date: "DD MMMM YYYY", msgs: [...] }`.
 */
export function groupMessagesByDate(messages) {
  const g = {};
  messages.forEach((m) => {
    const d = m.timestamp
      ? new Date(m.timestamp).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "Unknown";
    if (!g[d]) g[d] = [];
    g[d].push(m);
  });
  return Object.entries(g).map(([date, msgs]) => ({ date, msgs }));
}
